/* WHO THIS OUTREACH GOES TO — the rep's selection, and both gates.
 *
 * Brian: "what's the CTA for my team. Email going out with an agent created but
 * gated sequence. Those type of items must be selectable."
 * Brian: "A toggle that allows the gate of one person or to the group. Give our
 * team both."
 *
 * The panel above this one surfaces the AD, the administrators, the assistants
 * and the faculty at a school that just won something. Surfacing them is only
 * half a feature — until there is a CTA, a rep reads a list of names and then
 * opens Gmail and types the email by hand.
 *
 * ⚠️ THIS COMPONENT DOES NOT DECIDE ANYTHING. It collects ticks, a mode and an
 * optional addressee and posts them. Who the message is addressed to, how it is
 * deduped, and how the tier redirect applies are all decided ONCE, on the
 * server, in `outreach-recipients.js`. A second copy of those rules living here
 * would drift, and the failure mode of drift in this particular file is an
 * email to the wrong person.
 */
import { useEffect, useMemo, useState } from 'react'
import { api } from '../../lib/api'
import {manualRecipientIntent,suggestionSource} from '../../lib/manualRecipientIntent.js'
import styles from './OutreachPicker.module.css'

/* The identity used for ticking and for naming the addressee. It matches the
 * server's key exactly (`email || name:<lowercased name>`) — this is the one
 * thing that MUST agree, so it is stated once and asserted by the test. */
export const personKey = (p) =>
  String(p?.email || '').trim().toLowerCase() || `name:${String(p?.name || '').trim().toLowerCase()}`

/* ⚠️ LOCAL FALLBACK ONLY. The server owns this list (`GET /api/outreach/angles`);
 * these exist so the picker still works when that route is unreachable, and the
 * UI says which it is using. Same rule the artwork studio follows for its
 * config: never present a stale local menu as the engine's. */
export const FALLBACK_ANGLES = [
  { id: 'champion-congratulations', label: 'Champion — congratulations', who: 'A school that just won.' },
  { id: 'runner-up-and-contenders', label: 'Runner-up & contenders', who: 'Lost the final or had a milestone season. They buy rings too.' },
  { id: 'reorder', label: 'Reorder / add-on', who: 'An existing customer — players who missed the order, coaches, parents.' },
  { id: 'season-intro', label: 'New-season introduction', who: 'Pre-season. Get on the calendar before the championship.' },
  { id: 'quote-follow-up', label: 'Quote follow-up', who: 'A quote is out and unanswered.' },
  { id: 'program-partnership', label: 'Program / partnership', who: 'Multi-year: letterwinner, cheer gym, whole department.' },
  { id: 'recognition-program', label: 'Employee recognition program', who: 'Corporate service awards and milestones.' },
]

export default function OutreachPicker({ school, coach, committee, sport, championshipId, suggestions = null, reach = null, source = 'school-drawer', onSelectionChange = null, selectionOnly = false, embedded = false }) {
  const [picked, setPicked] = useState(() => new Set())  // ⚠️ EMPTY. Nobody by default.
  const [mode, setMode] = useState('group')
  const [manualPeople,setManualPeople]=useState([])
  const [manualEmail,setManualEmail]=useState('')
  const [manualAcknowledged,setManualAcknowledged]=useState(false)
  const [pendingSource,setPendingSource]=useState(null)
  const manualContext=JSON.stringify([championshipId,school?.id,sport])
  useEffect(()=>{setManualPeople([]);setPicked(new Set());setManualEmail('');setPendingSource(null);setManualAcknowledged(false)},[manualContext])
  /* ⚠️ NO DEFAULT ANGLE. Pre-selecting "champion congratulations" would file a
   * corporate or runner-up campaign under a segment nobody chose, and it would
   * look deliberate. The server refuses an unknown angle and pre-flight blocks
   * a campaign with none, so an unmade choice stays visibly unmade. */
  const [angle, setAngle] = useState('')
  const [angles, setAngles] = useState({ list: FALLBACK_ANGLES, live: false })
  /* ⚠️ THE CALL OFFER AND ITS LINK ARE TWO FIELDS, because they fail
   * differently. `offerCall` is intent; `meetingLink` is whether the intent can
   * be acted on. "Book a time" over a dead link is worse than never offering —
   * it spends the one moment the customer was willing. */
  const [offerCall, setOfferCall] = useState(false)
  const [meetingLink, setMeetingLink] = useState(() => {
    /* Remembered per browser so a rep types their scheduling link once, not on
     * every campaign. It is the rep's own link, not customer data. */
    try { return localStorage.getItem('sr_meeting_link') || '' } catch { return '' }
  })
  const linkOk = /^https:\/\//i.test(meetingLink.trim())

  useEffect(() => {
    if (selectionOnly || embedded) return undefined
    let alive = true
    api.outreachAngles()
      .then((r) => { if (alive && Array.isArray(r?.angles) && r.angles.length) setAngles({ list: r.angles, live: true }) })
      .catch(() => { /* keep the fallback; the banner below says so */ })
    return () => { alive = false }
  }, [selectionOnly,embedded])
  const [primaryKey, setPrimaryKey] = useState('')       // '' = let the server suggest
  const [state, setState] = useState({ status: 'idle' })

  /* Every person on the screen, in the order they are shown, each tagged with
   * the role group they came from — the group IS the role, so a rep never has
   * to retype it and the server can write per-role copy. */
  const people = useMemo(() => {
    /* ⚠️ WHEN THE SERVER HAS RANKED THEM, USE ITS ORDER AND ITS REASONS.
     *
     * `coach-suggest.js` returns ONE ordered list carrying, per person, the
     * band, the ask and the WHY. Re-deriving an order here would give the two
     * halves of the screen two different opinions about who to write to first,
     * and the reason strings are the part that lets a rep overrule the order at
     * all — a rank with no reason is just a different arbitrary default. The
     * grouped fallback below is kept for the school drawer and for any response
     * served before the ranking shipped, so neither caller breaks. */
    if (Array.isArray(suggestions) && suggestions.length) {
      return suggestions.map((s) => ({
        contactId:s.contactId||null,
        name: s.name, email: s.email || '', role: s.role || '', title: s.title || '',
        why: s.why, ask: s.ask, rank: s.rank, bandId: s.bandId,
        genderConflict: !!s.genderConflict, note: s.note || '',
      }))
    }
    const out = []
    const add = (list, role) => (list || []).forEach((p) => p?.name && out.push({ ...p, role }))
    if (coach?.name) add([coach], 'Head Coach')
    add(coach?.alternates, 'Head Coach')
    add(committee?.athleticDirectors, 'Athletic Director')
    add(committee?.administrators, 'Administrator')
    add(committee?.assistants, 'Assistant Coach')
    add(committee?.staff, 'Staff')
    add(committee?.otherHeadCoaches, 'Head Coach')
    /* One person can appear in two groups — a head coach who is also the AD is
     * one human. Dedupe for DISPLAY too, or the rep sees a name twice and
     * ticking either box looks like it did nothing to the other. */
    const seen = new Set()
    return out.filter((p) => { const k = personKey(p); if (seen.has(k)) return false; seen.add(k); return true })
  }, [coach, committee, suggestions])

  const chosen = manualPeople.filter((p) => p.context===manualContext&&picked.has(personKey(p)))
  const beginManual=(person=null)=>{
    setManualEmail(person?.email||'');setManualAcknowledged(false)
    setPendingSource(person?suggestionSource(person,school,championshipId):null)
  }
  const addManual=()=>{
    const email=manualEmail.trim().toLowerCase()
    if(!manualAcknowledged||!/^[^\s@<>;,]+@[^\s@<>;,]+\.[^\s@<>;,]+$/.test(email))return
    const person={name:email,email,role:'Manually chosen recipient',title:'Unverified',manualChoice:pendingSource?'suggestion-override':'entered',suggestionSource:pendingSource,context:manualContext}
    setManualPeople(rows=>[...rows.filter(p=>p.email!==email),person]);setPicked(rows=>new Set([...rows,email]));beginManual()
  }
  /* ⚠️ THE SELECTION IS REPORTED UPWARD, AND THE PICKER STILL OWNS IT.
   *
   * The crown dossier needs to know who is ticked, because its Gmail draft was
   * addressed to `dossier.coach.email` and nothing else — one person, the head
   * coach, who Brian's standing note says is usually not the buyer. Lifting the
   * state out of here would mean two components could disagree about who is
   * selected; a read-only callback cannot. */
  useEffect(() => {
    if (typeof onSelectionChange !== 'function') return
    onSelectionChange({
      mode, primaryKey,
      people: chosen.map((p) => ({...p})),
      recipientIntent:manualRecipientIntent(championshipId,chosen),
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [picked, mode, primaryKey, people,manualPeople,manualContext])
  /* ⚠️ SOMEBODY WITH NO EMAIL IS SHOWN AND DISABLED, WITH THE REASON — never
   * hidden. A rep who cannot find the AD in this list will assume the load
   * missed them and go looking; the truth is we hold the person and not their
   * address, which is a different job (and a real gap worth seeing). */
  const mailable = chosen.filter((p) => p.email)
  const unmailable = chosen.filter((p) => !p.email)

  const toggle = (p) => setPicked((s) => {
    const k = personKey(p), n = new Set(s)
    n.has(k) ? n.delete(k) : n.add(k)
    /* If the addressee is unticked, fall back to the server's suggestion rather
     * than silently addressing it to somebody the rep just removed. */
    if (!n.has(primaryKey)) setPrimaryKey('')
    return n
  })

  /* The chosen angle's own words, for the button. Lower-cased so it reads as a
   * sentence: "Stage 1 reorder email to 4". */
  const angleLabel = (angles.list.find((a) => a.id === angle)?.label || '')
    .replace(/\s*—.*$/, '').toLowerCase()

  async function stage() {
    if (selectionOnly || embedded) return
    setState({ status: 'working' })
    try {
      const r = await api.outreachStage({
        championshipId,
        schoolId: school?.id || null,
        sport: sport || null,
        mode,
        angle,
        offerCall,
        meetingLink: offerCall ? meetingLink.trim() : null,
        primaryKey: primaryKey || null,
        recipients: chosen.map((p) => ({ name: p.name, email: p.email || '', role: p.role, title: p.title || '' })),
        recipientIntent:manualRecipientIntent(championshipId,chosen),
        /* Which door staged it. The chain report needs this: every staged
         * campaign in production carried `recipients: 0` because the ONLY door
         * that selects recipients was this one, in the school drawer, while the
         * crown dossier staged copy and addressed nobody. */
        source,
      })
      setState(r?.ok ? { status: 'staged', r } : { status: 'error', detail: r?.error || 'stage refused' })
    } catch (e) {
      setState({ status: 'error', detail: e?.message || String(e) })
    }
  }

  return (
    <div className={styles.wrap}>
      <div className={styles.head}>
        <b>Draft outreach — choose who</b>
        <span className={styles.hint}>
          Suggestions remain unselected. Choose a verified coach in recipient eligibility review, or explicitly review an unverified manual address below. Nothing sends here.
          {selectionOnly&&' No ring pitch or artwork attachment is included.'}
        </span>
        {/* ⚠️ THE ARITHMETIC, ON SCREEN. The old count said "3 other people ARE
            contactable" at a school holding 12 people and 10 addresses, because
            it excluded staff and other-sport coaches while counting people with
            no email. A corrected number with no stated basis is just a
            different number to doubt, so the sentence carries its own
            reconciliation. */}
        {reach && <span className={styles.hint}>{reach.reconciles}</span>}
      </div>

      <div className={styles.list}>
        {people.map((p) => {
          const k = personKey(p)
          return (
            <div className={`${styles.row} ${p.email ? '' : styles.rowOff}`} key={k}>
              <input type="checkbox" aria-label={`Suggested contact ${p.name}`} checked={false} disabled />
              <span className={styles.who}>
                <b>{p.name}</b>
                <em>{p.role}</em>
                {p.title && <span>{p.title}</span>}
              </span>
              {p.email
                ? <span className={styles.addr}>{p.email}</span>
                : <span className={styles.noAddr}>no email on file — can’t be included</span>}
              {/* ⚠️ THE REASON FOR THE RANK, NEXT TO THE TICKBOX.
                  Brian: "suggest the coaches to reach out to." A rank with no
                  reason is just a different arbitrary default, and a rep cannot
                  overrule a default they cannot see — which is how the head
                  coach silently became the only person anybody ever mailed. */}
              {p.why && (
                <span className={styles.why}>
                  <span>{p.rank ? <i className={styles.rank}>{p.rank}</i> : null}{p.why}</span>
                  {!selectionOnly && p.ask && <em>Ask: {p.ask}</em>}
                  {p.genderConflict && (
                    <strong className={styles.conflict}>
                      This title names the other team — check before writing to them for THIS championship.
                    </strong>
                  )}
                </span>
              )}
              <span className={styles.manualReview}>Suggestion only. Use recipient eligibility review for a verified coach selection.</span>
              {p.email&&<button className={styles.manualReview} type="button" onClick={()=>beginManual(p)}>Review manual override for {p.name}</button>}
            </div>
          )
        })}
      </div>

      {/* ⚠️ WHAT KIND OF OUTREACH IS THIS? — the question the panel was missing.
          Brian: "there is no cta and no selector of the style of outreach we
          want to use for segmentation and sequencing."

          `mode` below is the SHAPE of the send and `sequence` is the CADENCE;
          neither says what the email is FOR. The angle is what decides the
          SEGMENT this campaign lands in and how its follow-ups are paced, so it
          is asked FIRST — purpose before shape. */}
      <fieldset className={styles.manual}>
        <legend>Explicit manual recipients</legend>
        <p>Manual addresses are unverified. This does not confirm the school, sport or team, even when an address exists in the directory.</p>
        {pendingSource&&<p role="status">You are choosing a manual override of a displayed suggestion. Its source will be recorded; no coach verification is implied.</p>}
        <label>Business email <input type="email" value={manualEmail} onChange={e=>{setManualEmail(e.target.value);setManualAcknowledged(false)}} /></label>
        <label><input type="checkbox" checked={manualAcknowledged} onChange={e=>setManualAcknowledged(e.target.checked)} /> I choose this unverified business address for this outreach.</label>
        <button type="button" disabled={!manualAcknowledged||!manualEmail.trim()} onClick={addManual}>Add manual recipient</button>
        <button type="button" onClick={()=>beginManual()}>Clear manual entry</button>
        {manualPeople.filter(p=>p.context===manualContext).map(p=><label key={p.email}><input type="checkbox" checked={picked.has(personKey(p))} onChange={()=>toggle(p)} />{p.email} · Unverified manual {p.manualChoice==='suggestion-override'?'override':'entry'}</label>)}
      </fieldset>
      {!selectionOnly && !embedded && <><div className={styles.angles}>
        <span className={styles.sectionHead}>
          What kind of outreach is this?
          {!angles.live && <em className={styles.stale}> · offline list — the server’s angles could not be loaded</em>}
        </span>
        <div className={styles.angleGrid}>
          {angles.list.map((a) => (
            <label key={a.id} className={`${styles.angle} ${angle === a.id ? styles.angleOn : ''}`}>
              <input type="radio" name="outreach-angle" checked={angle === a.id}
                onChange={() => setAngle(a.id)} />
              <span><b>{a.label}</b><em>{a.who}</em>
                {Array.isArray(a.steps) && a.steps.length > 0 && (
                  <i className={styles.cadence}>
                    {a.steps.length} touch{a.steps.length === 1 ? '' : 'es'} · day {a.steps.join(', ')}
                  </i>
                )}
              </span>
            </label>
          ))}
        </div>
      </div>

      {/* ⚠️ SEEING IS BELIEVING — the scheduled design-studio call.
          Brian: "I want there to be an email in the sequence that immediately
          lets our potential customers know we can take a scheduled video call
          using our design studio to show off real rings and products."

          Set your HubSpot scheduling page's MINIMUM NOTICE to 24 or 48 hours
          and a customer physically cannot book inside your window — which is
          the thing that stops a rep sitting at a desk waiting. */}
      <div className={styles.offer}>
        <label className={styles.offerRow}>
          <input type="checkbox" checked={offerCall} onChange={() => setOfferCall((v) => !v)} />
          <span>
            <b>Offer a design-studio video call</b>
            <em>Adds the invitation to the sequence — real rings, on camera.</em>
          </span>
        </label>
        {offerCall && (
          <label className={styles.linkRow}>
            Your scheduling link
            <input type="url" placeholder="https://meetings.hubspot.com/…"
              value={meetingLink}
              onChange={(e) => {
                setMeetingLink(e.target.value)
                try { localStorage.setItem('sr_meeting_link', e.target.value) } catch { /* private mode */ }
              }} />
            {/* The check is stated before the send, not discovered after it. */}
            {!linkOk && (
              <em className={styles.linkWarn}>
                {meetingLink.trim()
                  ? 'That is not an https link — the invitation would not open.'
                  : 'Needed before this can send. An unbookable invitation is worse than none.'}
              </em>
            )}
          </label>
        )}
      </div>

      </>}

      {/* ⚠️ BOTH GATES, because Brian asked for both and they are genuinely
          different sends. Group = one thread, a primary and the rest copied.
          Per-person = one email each, written for that role: the AD is asked to
          approve, the assistant to handle sizing, the head coach to confirm the
          roster. */}
      <div className={styles.controls}>
        <div className={styles.modes}>
          {[['group', 'One email to the group', 'a primary, everyone else copied'],
            ['per-person', 'One email each', 'written for that person’s role']].map(([id, label, why]) => (
            <label key={id} className={`${styles.mode} ${mode === id ? styles.modeOn : ''}`}>
              <input type="radio" name="outreach-mode" checked={mode === id} onChange={() => setMode(id)} />
              <span><b>{label}</b><em>{why}</em></span>
            </label>
          ))}
        </div>

        {/* ⚠️ THE ADDRESSEE IS PICKED, NOT DERIVED.
            Brian: "Sometimes its directed at the head coach but an assistant is
            included as it's an assistants role to handle this, from a secretary
            to an AD to a position coach. All different depending on the school."
            So the default is a SUGGESTION the rep can override, and the option
            list is only people they actually ticked. */}
        {mode === 'group' && mailable.length > 1 && (
          <label className={styles.addressee}>
            Addressed to
            <select value={primaryKey} onChange={(e) => setPrimaryKey(e.target.value)}>
              <option value="">Suggest — usually the head coach</option>
              {mailable.map((p) => (
                <option key={personKey(p)} value={personKey(p)}>{p.name} · {p.role}</option>
              ))}
            </select>
            <em>everyone else you ticked is copied</em>
          </label>
        )}
      </div>

      {!selectionOnly && !embedded && <div className={styles.foot}>
        {/* ⚠️ THE CTA NAMES THE ACT AND ITS SEGMENT. "Draft outreach" told a rep
            nothing about what would happen or which campaign it joined. It now
            reads back the angle they chose, so the button is a sentence rather
            than a label — and it is disabled until the angle IS chosen, because
            the server's pre-flight blocks an unsegmented campaign anyway and
            finding that out after clicking is a worse way to learn it. */}
        <button className={styles.cta}
          disabled={!mailable.length || !angle || (offerCall && !linkOk) || state.status === 'working'} onClick={stage}>
          {state.status === 'working'
            ? 'Staging…'
            : !angle
              ? 'Choose an outreach type first'
              : offerCall && !linkOk
                ? 'Add your scheduling link'
              : mode === 'group'
                ? `Stage 1 ${angleLabel} email to ${mailable.length}`
                : `Stage ${mailable.length} ${angleLabel} email${mailable.length > 1 ? 's' : ''}`}
        </button>
        <span className={styles.count}>
          {mailable.length
            ? (mode === 'group'
                ? `1 email · ${mailable.length} recipient${mailable.length > 1 ? 's' : ''}`
                : `${mailable.length} email${mailable.length > 1 ? 's' : ''}, one each`)
            : 'Tick at least one person with an email address'}
          {unmailable.length > 0 && ` · ${unmailable.length} selected without an address`}
        </span>
      </div>}

      {/* The verdict, in the server's own words. A staged draft is NOT a send —
          say so plainly, because "it's gone out" is the assumption a green
          confirmation invites. */}
      {!selectionOnly && state.status === 'staged' && (
        <div className={styles.ok}>
          Staged for review — <b>nothing has been sent</b>. {state.r.messages} message
          {state.r.messages === 1 ? '' : 's'} · tier <b>{state.r.rollout}</b>
          {state.r.rollout !== 'full' && ' (redirected to the internal test inbox — real recipients are not reachable at this tier)'}
          {state.r.blockers?.length > 0 && (
            <div className={styles.blockers}>
              {state.r.blockers.map((b) => <span key={b.id}>⚠️ {b.detail}</span>)}
            </div>
          )}
        </div>
      )}
      {!selectionOnly && state.status === 'error' && <div className={styles.err}>Could not stage: {state.detail}</div>}
    </div>
  )
}
